import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { TodoItem, TaskReminder } from '../../../../modules/TodoItem'
import { updateTodoReminder } from '../../../../modules/TodoUpdater'
import { DatePickerType } from '../../../DatePicker/DatePickerType'

interface UseReminderHandlerProps {
  taskInstance: TodoItem | null
  isReadOnly: boolean
  startDate: Date | null
  onTodoUpdate?: () => void
  onOpenDatePicker?: (
    type: string,
    date: Date,
    taskInstance: TodoItem,
    callback: (date: Date) => void,
  ) => void
}

interface UseReminderHandlerReturn {
  reminderDropdownOpen: boolean
  setReminderDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleReminderClick: (e: React.MouseEvent) => void
  handleReminderChange: (value: string | Date | null, isDateTime?: boolean) => void
  handleReminderPickDateClick: () => void
}

/**
 * 리마인더 처리를 위한 커스텀 훅
 */
export const useReminderHandler = ({
  taskInstance,
  isReadOnly,
  startDate,
  onTodoUpdate,
  onOpenDatePicker,
}: UseReminderHandlerProps): UseReminderHandlerReturn => {
  const [reminderDropdownOpen, setReminderDropdownOpen] = useState<boolean>(false)

  const handleReminderClick = (e: React.MouseEvent) => {
    if (isReadOnly) return
    e.stopPropagation()
    setReminderDropdownOpen(true)
  }

  const handleReminderChange = (value: string | Date | null, isDateTime: boolean = false) => {
    if (!taskInstance || isReadOnly) return

    let alarms: TaskReminder[] = []
    let updatedStartDate: Date | null = startDate

    if (value === null) {
      alarms = []
    } else if (isDateTime) {
      // 절대 시간 알람 설정
      const reminderDateTime: Dayjs | null = value instanceof Date ? dayjs(value) : null

      if (!reminderDateTime || !reminderDateTime.isValid()) {
        console.warn('Invalid reminder date:', value)
        return
      }

      const formattedTime = reminderDateTime.utc().format('YYYYMMDD[T]HHmmss[Z]')

      // reminder 시간이 설정되면 startDate를 업데이트
      let targetDateTime = reminderDateTime.second(0)
      if (targetDateTime.isUTC()) {
        targetDateTime = targetDateTime.local()
      }

      const shouldUpdateStart =
        !startDate ||
        dayjs(startDate).format('YYYY-MM-DD HH:mm') !== targetDateTime.format('YYYY-MM-DD HH:mm')

      if (shouldUpdateStart) {
        updatedStartDate = targetDateTime.toDate()
      }

      alarms = [
        {
          action: 'DISPLAY',
          trigger: {
            params: {},
            val: formattedTime,
          },
        },
      ]
    } else {
      // 상대 시간 알람 설정
      alarms = [{ action: 'DISPLAY', trigger: value as string }]
    }

    updateTodoReminder(taskInstance, alarms, updatedStartDate, (success: boolean) => {
      if (success && onTodoUpdate) {
        onTodoUpdate()
      }
    })
    setReminderDropdownOpen(false)
  }

  const handleReminderPickDateClick = () => {
    if (isReadOnly) return
    setReminderDropdownOpen(false)
    handleReminderDatePickerClose()
  }

  const handleReminderDatePickerClose = () => {
    if (isReadOnly) return
    if (onOpenDatePicker && taskInstance) {
      onOpenDatePicker(
        DatePickerType.DATETIME,
        startDate || new Date(),
        taskInstance,
        (dateTime: Date) => {
          handleReminderChange(dateTime, true)
        },
      )
    }
  }

  return {
    reminderDropdownOpen,
    setReminderDropdownOpen,
    handleReminderClick,
    handleReminderChange,
    handleReminderPickDateClick,
  }
}
